import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { environment } from "../../environments/environment";
import { GateInRequest } from "../models/gate-in-request.interface";
import { TableListRequest } from "../models/table-list-request.interface";
import { Observable } from "rxjs";
import { ShippingBill } from "../models/shipping-bill.interface";
import { APIResponse } from "../models/standard-response.interface";
import { ExBOERequest } from "../components/documentation/ex-boe-filing/manage-ex-boe/manage-ex-boe.component";
import { CommonUtils } from "../utils/common-utils";

const containerInventoryEndPoint = "v1/containers";
const containerInventoryEndPointV2 = "v2/containers";
const vehicleInventoryEndPoint = "v1/inventory/trailers";
const importCargoItemEndPoint = "v1/import-cargo-items";
const shippingBillEndPoint = "v1/shipping-bill-items";
const documentsEndPoint = "v1/documents";
const forwardingNoteEndPoint = "v1/forwarding-notes";
const cargoItemEndPoint = "v1/items";


@Injectable({
  providedIn: "root"  // Ensures the service is available app-wide
})
export class InventoryService {
  private baseUrl: string = environment.apiBaseUrl;

  constructor(private http: HttpClient) {
  }


  // VEHICLE APIs
  getVehicleList(tableListRequest: TableListRequest) {
    return this.http.post(`${this.baseUrl}/${vehicleInventoryEndPoint}/list`, tableListRequest);
  }

  getVehicleByID(vehicleId: string): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.baseUrl}/${vehicleInventoryEndPoint}/${vehicleId}`);
  }

  getVehicleByInventoryNo(vehicleNo: string): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.baseUrl}/${vehicleInventoryEndPoint}/fetch-by-vehicle-no/${vehicleNo}`);
  }

  getVehicleGateOutDetails(vehicleId: string, chaId?: string): Observable<APIResponse> {
    let url = `${this.baseUrl}/${vehicleInventoryEndPoint}/${vehicleId}/exit-details`;
    if (chaId) {
      url += `?cha=${chaId}`;
    }
    return this.http.get<APIResponse>(url);
  }

  fetchRecentlyGatedOutTrailerContainers(trailerNo: string): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.baseUrl}/${vehicleInventoryEndPoint}/${trailerNo}/recent-gate-out`);
  }

  // CONTAINER APIs

  getContainerList(tableListRequest: TableListRequest): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${containerInventoryEndPoint}/list?useNewContract=true`, tableListRequest);
  }

  getContainerById(containerId: string, withItems: boolean = false, includeHoldDetails: boolean = true, includeEventLogs: boolean = false): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.baseUrl}/${containerInventoryEndPoint}/${containerId}?withItems=${withItems}&includeHoldDetails=${includeHoldDetails}&includeEventLogs=${includeEventLogs}`);
  }

  getContainerIgmData(containerNo: string, containerId?: string): Observable<APIResponse> {
    let url = `${this.baseUrl}/${containerInventoryEndPoint}/${containerNo}/igm-data`;

    if (containerId) {
      url += `?containerId=${containerId}`;
    }

    return this.http.get<APIResponse>(url);
  }

  getContainerActivity(containerId: string): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.baseUrl}/${containerInventoryEndPoint}/${containerId}/activity`);
  }

  updateContainer(containerId: string, container: any) {
    return this.http.put(
      `${this.baseUrl}/${containerInventoryEndPoint}/${containerId}`,
      container
    );
  }

  updateForwardingNote(forwardingNoteRequest: any) {
    return this.http.post(
      `${this.baseUrl}/${forwardingNoteEndPoint}`,
      forwardingNoteRequest
    );
  }

  fetchContainersForForwardingNote(request: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/${forwardingNoteEndPoint}/fetch-by-document`, request);
  }

  fetchContainerByDocument(request: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/${containerInventoryEndPoint}/fetch-by-document`, request);
  }

  searchContainers(containerSearchRequest: any): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${containerInventoryEndPoint}/search`, containerSearchRequest);
  }

  transferContainers(transferRequest: any): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${containerInventoryEndPoint}/transfers`, transferRequest);
  }

  markForceGateOutContainers(markForceGateOutRequest: any): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${containerInventoryEndPoint}/force-gateout/mark`, markForceGateOutRequest);
  }

  closeForceGateOutContainers(closeForceGateOutRequest: any): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${containerInventoryEndPoint}/force-gateout/close`, closeForceGateOutRequest);
  }

  // Container APIs V2
  getContainerListV2(tableListRequest: TableListRequest): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${containerInventoryEndPointV2}/list`, tableListRequest);
  }

  getContainerByIdV2(containerId: string, includeItems: boolean = false, includeEventLogs: boolean = false): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.baseUrl}/${containerInventoryEndPointV2}/${containerId}?includeItems=${includeItems}&includeEventLogs=${includeEventLogs}`);
  }


  // CARGO APIs

  fetchCargoByDocument(request: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/${cargoItemEndPoint}/fetch-by-document`, request);
  }

  saveExBoe(request: ExBOERequest): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${cargoItemEndPoint}/ex-boe-filing`, request);
  }

  updateExBoe(id: string, request: ExBOERequest): Observable<APIResponse> {
    return this.http.put<APIResponse>(`${this.baseUrl}/${cargoItemEndPoint}/ex-boe-filing/${id}`, request);
  }

  saveShippingBill(shippingBill: ShippingBill): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${shippingBillEndPoint}`, shippingBill);
  }

  fetchShippingBillById(shippingBillId: string): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.baseUrl}/${shippingBillEndPoint}/${shippingBillId}`);
  }

  updateShippingBill(shippingBillId: string, shippingBill: ShippingBill): Observable<APIResponse> {
    return this.http.put<APIResponse>(`${this.baseUrl}/${shippingBillEndPoint}/${shippingBillId}`, shippingBill);
  }

  listShippingBill(tableListRequest: TableListRequest): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${shippingBillEndPoint}/list`, tableListRequest);
  }

  listCargoItems(tableListRequest: TableListRequest): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${cargoItemEndPoint}/list`, tableListRequest);
  }

  updateLeo(leoUpdateRequestList: any) {
    return this.http.put<APIResponse>(`${this.baseUrl}/${documentsEndPoint}/leo`, leoUpdateRequestList);
  }

  updateCooc(leoUpdateRequestList: any) {
    return this.http.put<APIResponse>(`${this.baseUrl}/${documentsEndPoint}/cooc`, leoUpdateRequestList);
  }

  fetchItemByID(itemId: string, withContainers: boolean = false, includeHoldDetails: boolean = false, includeEventLogs: boolean = false): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.baseUrl}/${cargoItemEndPoint}/${itemId}?withContainers=${withContainers}&includeHoldDetails=${includeHoldDetails}&includeEventLogs=${includeEventLogs}`);
  }

  parseBOE(formData: FormData): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${importCargoItemEndPoint}/parse-boe`, formData);
  }

  parseShippingBill(formData: FormData): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${shippingBillEndPoint}/parse-shipping-bill`, formData);
  }

  findCargoByVesselAndItemNo(vesselId: string, itemNo: string, subItemNo: string): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.baseUrl}/${importCargoItemEndPoint}?vesselId=${vesselId}&itemNo=${itemNo}&subItemNo=${subItemNo}`);
  }

  issueNocForBonding(cargoId: string, request: {}): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${importCargoItemEndPoint}/${cargoId}/bonding-noc`, request);
  }

  updateBondPermission(cargoId: string, request: {}): Observable<APIResponse> {
    return this.http.patch<APIResponse>(`${this.baseUrl}/${importCargoItemEndPoint}/${cargoId}/bonding-permission`, request);
  }

  downloadNoc(cargoId: string): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.baseUrl}/${importCargoItemEndPoint}/${cargoId}/bonding-noc/print-pdf`);
  }

  getEirData(containerNo: string, includeImages: boolean = false): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.baseUrl}/${containerInventoryEndPoint}/eir?containerNo=${containerNo}&includeImages=${includeImages}`);
  }

  getMostRecentlyGatedOutContainer(containerNo: string): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.baseUrl}/${containerInventoryEndPoint}/most-recently-gated-out/${containerNo}`);
  }

  bulkUpdateContainerAccountHolder(bulkAccountHolderReq: { documentId: any; accountHolder: any }) {
    return this.http.put<APIResponse>(`${this.baseUrl}/${containerInventoryEndPoint}/update-account-holder`, bulkAccountHolderReq);
  }
}
